import { useCallback, useEffect, useRef, useState } from 'react'
import { postTranscribeVoice } from '../api/apiConfig'
import { clientReminderRuntimePayload } from '../utils/reminderWorkflow'

/** AWS Transcribe streaming: 48 kHz mono 16-bit PCM (matches typical browser mic / batch WebM quality). */
const DEFAULT_SAMPLE_RATE = 48000
/** 50 ms of mono 16-bit PCM — AWS-recommended chunk size at 48 kHz. */
const STREAM_PCM_CHUNK_MS = 50
const BATCH_SESSION_WAIT_MS = 120000
/** Batch: end sooner after silence (streaming default is 1800 ms). */
const BATCH_SILENCE_AUTO_STOP_MS = 1200
export type TranscribeMode = 1 | 2

/** Log which AWS Transcribe path the widget will use (streaming WebSocket vs batch HTTP). */
export function logAwsTranscribeMode(
  mode: TranscribeMode,
  context?: { apiBaseUrl?: string; rawFromVersion?: number },
): void {
  const api = (context?.apiBaseUrl ?? '').trim()
  const versionHint =
    context?.rawFromVersion != null
      ? ` (from GET ${api || '…'}/version → transcribe_mode=${context.rawFromVersion})`
      : ''
  if (mode === 2) {
    console.log(
      `[VoiceNav] AWS Transcribe: batch (TRANSCRIBE_MODE=2, HTTP POST /transcribe/voice)${versionHint}`,
    )
  } else {
    console.log(
      `[VoiceNav] AWS Transcribe: streaming (TRANSCRIBE_MODE=1, WebSocket /ws/transcribe, ` +
        `${DEFAULT_SAMPLE_RATE} Hz PCM)${versionHint}`,
    )
  }
}

const SESSION_WAIT_MS = 35000
const RECOVERABLE_MIC_RETRY_MS = 350

/** End recording after this many ms below the RMS threshold once speech has been detected. */
const DEFAULT_SILENCE_AUTO_STOP_MS = 1800
/** Ignore silence auto-stop for this long after recording starts (setup noise). */
const DEFAULT_SILENCE_GRACE_MS = 650
/** RMS threshold on input float samples (-1..1) to count as speech. */
const DEFAULT_SILENCE_RMS_THRESHOLD = 0.014
/** Mic boost when capturing speaker playback (gTTS loopback test). */
const SPEAKER_CAPTURE_MIC_GAIN = 3

export type StartRecordingOptions = {
  /** gTTS / speaker loopback: disable AEC and silence auto-stop, boost mic gain. */
  speakerPlaybackCapture?: boolean
}

export type VoiceStreamStatus =
  | 'idle'
  | 'requesting_permission'
  | 'connecting'
  | 'recording'
  | 'transcribing'
  | 'error'

export type AssistantPatientOption = { selection_id: string; label: string; dob: string; gender?: string }

export type AssistantChatMessage = {
  role: string
  text: string
  metadata?: Record<string, unknown>
  ui_type?: string
  options?: AssistantPatientOption[]
}

type Args = {
  websocketUrl: string
  apiBaseUrl?: string
  transcribeMode?: TranscribeMode
  clientId?: string
  tenantId?: string
  /** Correlates typed + voice turns in backend transcript audit (in-memory per widget mount). */
  sessionId?: string
  /** Fresh credentials for each voice session (sessionStorage + defaults). */
  getMedicalLinkCredentials?: () => {
    piBearerToken: string
    loggedUserId: string
    medCenterId: string
    senderFbUid?: string
  }
  languageOptions?: string[]
  preferredLanguage?: string
  sampleRate?: number
  /** After speech is detected, stop if quieter than threshold for this long. `false` disables. */
  silenceAutoStopMs?: number | false
  silenceRmsThreshold?: number
  silenceGraceMs?: number
  onPartial?: (text: string) => void
  /** Fired after AWS transcribe + ASR normalization; command can appear in chat before Nova intent. */
  onTranscriptReady?: (
    displayText: string,
    fullTranscript: string,
    transcribeProcessingMs?: number,
  ) => void
  onFinal: (
    text: string,
    assistantMessages?: AssistantChatMessage[],
    llmTokenUsage?: Record<string, unknown> | null,
    transcribeProcessingMs?: number,
    intentProcessingMs?: number,
  ) => void | Promise<void>
  onError: (message: string) => void
  finishingTranscriptionHint?: string
  intentProcessingHint?: string
  /** When true, only stream to AWS Transcribe and return ``full_transcript`` (no chat pipeline). */
  transcriptionOnly?: boolean
  /** When a document preview is open, sent on WebSocket `start` for ``mail_document`` validation. */
  getActiveDocumentContext?: () => {
    id: string
    name: string
    url: string
    mime: string
    examDate?: string
    folderName?: string
  }
}

function linearResample(float32: Float32Array, fromRate: number, toRate: number): Float32Array {
  if (fromRate === toRate) return float32
  const outLen = Math.max(1, Math.floor((float32.length * toRate) / fromRate))
  const out = new Float32Array(outLen)
  const ratio = fromRate / toRate
  for (let i = 0; i < outLen; i++) {
    const srcPos = i * ratio
    const j = Math.floor(srcPos)
    const f = srcPos - j
    const a = float32[j] ?? 0
    const b = float32[j + 1] ?? a
    out[i] = a * (1 - f) + b * f
  }
  return out
}

function floatTo16BitPCM(float32: Float32Array): ArrayBuffer {
  const buffer = new ArrayBuffer(float32.length * 2)
  const view = new DataView(buffer)
  for (let i = 0; i < float32.length; i++) {
    const s = Math.max(-1, Math.min(1, float32[i]))
    view.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true)
  }
  return buffer
}

/** Bytes per WebSocket PCM chunk: (chunkMs / 1000) * sampleRate * 2 (16-bit mono). */
function streamingPcmChunkBytes(sampleRateHz: number): number {
  return Math.max(320, Math.floor((sampleRateHz * 2 * STREAM_PCM_CHUNK_MS) / 1000))
}

function resetPcmSendBuffer(bufferRef: { current: Uint8Array }): void {
  bufferRef.current = new Uint8Array(0)
}

function enqueueStreamingPcm(
  ws: WebSocket,
  bufferRef: { current: Uint8Array },
  pcm: ArrayBuffer,
  chunkBytes: number,
): void {
  const incoming = new Uint8Array(pcm)
  const prev = bufferRef.current
  const merged = new Uint8Array(prev.length + incoming.length)
  merged.set(prev)
  merged.set(incoming, prev.length)
  let offset = 0
  while (offset + chunkBytes <= merged.length) {
    ws.send(merged.subarray(offset, offset + chunkBytes))
    offset += chunkBytes
  }
  bufferRef.current = offset < merged.length ? merged.subarray(offset) : new Uint8Array(0)
}

function flushStreamingPcm(ws: WebSocket, bufferRef: { current: Uint8Array }): void {
  const rest = bufferRef.current
  if (rest.length > 0 && ws.readyState === WebSocket.OPEN) {
    ws.send(rest)
  }
  bufferRef.current = new Uint8Array(0)
}

function logStreamingRecordingStarted(args: {
  micSampleRateHz?: number
  audioContextSampleRateHz: number
  targetSampleRateHz: number
  pcmChunkBytes: number
  preferredLanguage: string
}): void {
  console.log(
    `[VoiceNav] Streaming → AWS Transcribe: target=${args.targetSampleRateHz} Hz PCM, ` +
      `AudioContext=${args.audioContextSampleRateHz} Hz, mic=${args.micSampleRateHz ?? 'unknown'} Hz, ` +
      `chunk=${args.pcmChunkBytes} bytes (${STREAM_PCM_CHUNK_MS}ms), language=${args.preferredLanguage}.`,
  )
}

function bufferRms(float32: Float32Array): number {
  if (float32.length === 0) return 0
  let sum = 0
  for (let i = 0; i < float32.length; i++) {
    const x = float32[i]
    sum += x * x
  }
  return Math.sqrt(sum / float32.length)
}

function micAudioConstraints(speakerPlaybackCapture: boolean): boolean | MediaTrackConstraints {
  if (!speakerPlaybackCapture) return true
  return {
    echoCancellation: false,
    noiseSuppression: false,
    autoGainControl: true,
  }
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms)
  })
}

function pickRecorderMimeType(): string {
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/mp4']
  for (const mime of candidates) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(mime)) {
      return mime
    }
  }
  return ''
}

function extensionForMime(mime: string): string {
  if (mime.includes('ogg')) return 'ogg'
  if (mime.includes('mp4')) return 'm4a'
  return 'webm'
}

function micTrackSampleRateHz(stream: MediaStream): number | undefined {
  const track = stream.getAudioTracks()[0]
  const hz = track?.getSettings?.().sampleRate
  return typeof hz === 'number' && hz > 0 ? hz : undefined
}

function logBatchRecordingStarted(args: {
  micSampleRateHz?: number
  audioContextSampleRateHz: number
  mime: string
  widgetSampleRate: number
}): void {
  console.log(
    `[VoiceNav] Batch recording started: mic=${args.micSampleRateHz ?? 'unknown'} Hz, ` +
      `AudioContext=${args.audioContextSampleRateHz} Hz, mime=${args.mime || 'default'}. ` +
      `(widget sampleRate=${args.widgetSampleRate} is for streaming mode 1 only; batch uses the encoded file.)`,
  )
}

type BatchAudioInspect = {
  decodedSampleRateHz: number | null
  channels: number | null
  durationSec: number | null
  blobBytes: number
  mime: string
}

async function inspectBatchAudioBlob(blob: Blob, mime: string): Promise<BatchAudioInspect> {
  const out: BatchAudioInspect = {
    decodedSampleRateHz: null,
    channels: null,
    durationSec: null,
    blobBytes: blob.size,
    mime,
  }
  if (!blob.size || typeof AudioContext === 'undefined') {
    return out
  }
  const ctx = new AudioContext()
  try {
    const buf = await ctx.decodeAudioData(await blob.arrayBuffer())
    out.decodedSampleRateHz = buf.sampleRate
    out.channels = buf.numberOfChannels
    out.durationSec = buf.duration
  } catch {
    // Some WebM/Opus blobs fail decode in-browser; AWS still reads metadata from the file.
  } finally {
    await ctx.close().catch(() => {})
  }
  return out
}

async function logBatchUploadForAws(blob: Blob, mime: string): Promise<void> {
  const inspect = await inspectBatchAudioBlob(blob, mime)
  if (inspect.decodedSampleRateHz != null) {
    console.log(
      `[VoiceNav] Batch upload → AWS Transcribe: sampleRate=${inspect.decodedSampleRateHz} Hz ` +
        `(from decoded audio), channels=${inspect.channels}, duration=${inspect.durationSec?.toFixed(2)}s, ` +
        `bytes=${inspect.blobBytes}, mime=${inspect.mime}. AWS uses the file's sample rate, not .env.`,
    )
  } else {
    console.warn(
      `[VoiceNav] Batch upload → AWS Transcribe: could not decode sample rate in-browser ` +
        `(bytes=${inspect.blobBytes}, mime=${inspect.mime}). AWS will read sample rate from file metadata.`,
    )
  }
}

function buildVoiceMetadataPayload(args: {
  sampleRate: number
  languageOptions: string[]
  preferredLanguage: string
  clientId?: string
  tenantId?: string
  sessionId?: string
  getMedicalLinkCredentials?: Args['getMedicalLinkCredentials']
  getActiveDocumentContext?: Args['getActiveDocumentContext']
  transcriptionOnly: boolean
}): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    sample_rate: args.sampleRate,
    language_options: args.languageOptions,
    preferred_language: args.preferredLanguage,
    client_id: args.clientId,
    tenant_id: args.tenantId,
    session_id: args.sessionId,
    ...clientReminderRuntimePayload(),
  }
  const ml = args.getMedicalLinkCredentials?.()
  if (ml) {
    payload.pi_bearer_token = ml.piBearerToken
    payload.logged_user_id = ml.loggedUserId
    payload.med_center_id = ml.medCenterId
    const fb = (ml.senderFbUid ?? '').trim()
    if (fb) payload.sender_fb_uid = fb
  }
  const docCtx = args.getActiveDocumentContext?.()
  if (docCtx) {
    const did = (docCtx.id ?? '').trim()
    const dname = (docCtx.name ?? '').trim()
    const durl = (docCtx.url ?? '').trim()
    const dmime = (docCtx.mime ?? '').trim()
    const dex = (docCtx.examDate ?? '').trim()
    const dfolder = (docCtx.folderName ?? '').trim()
    if (did) payload.active_document_id = did
    if (dname) payload.active_document_name = dname
    if (durl) payload.active_document_url = durl
    if (dmime) payload.active_document_mime_type = dmime
    if (dex) payload.active_document_exam_date = dex
    if (dfolder) payload.active_document_folder = dfolder
  }
  if (args.transcriptionOnly) payload.transcription_only = true
  return payload
}

function describeMicrophoneAccessError(name: string): string {
  if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
    return 'Microphone permission was denied. Allow access in the browser address bar or site settings.'
  }
  if (name === 'NotFoundError') {
    return 'No microphone was found on this device.'
  }
  if (name === 'SecurityError') {
    return 'Microphone blocked for security reasons. Serve the page over HTTPS, not HTTP on a network IP.'
  }
  if (name === 'NotReadableError') {
    return 'Microphone is busy or temporarily unavailable. Close other tabs or apps using it, then try again.'
  }
  return 'Microphone permission denied or unavailable.'
}

export function useVoiceStreaming({
  websocketUrl,
  apiBaseUrl,
  transcribeMode = 1,
  clientId,
  tenantId,
  sessionId,
  getMedicalLinkCredentials,
  getActiveDocumentContext,
  languageOptions = ['fr-FR'],
  preferredLanguage = 'fr-FR',
  sampleRate = DEFAULT_SAMPLE_RATE,
  silenceAutoStopMs = DEFAULT_SILENCE_AUTO_STOP_MS,
  silenceRmsThreshold = DEFAULT_SILENCE_RMS_THRESHOLD,
  silenceGraceMs = DEFAULT_SILENCE_GRACE_MS,
  transcriptionOnly = false,
  finishingTranscriptionHint = 'Finishing…',
  intentProcessingHint = '',
  onPartial,
  onTranscriptReady,
  onFinal,
  onError,
}: Args) {
  const [status, setStatus] = useState<VoiceStreamStatus>('idle')
  const [statusHint, setStatusHint] = useState('')

  const statusRef = useRef<VoiceStreamStatus>('idle')
  useEffect(() => {
    statusRef.current = status
  }, [status])

  const wsRef = useRef<WebSocket | null>(null)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const recorderChunksRef = useRef<Blob[]>([])
  const recorderMimeRef = useRef('')
  const activeTranscribeModeRef = useRef<TranscribeMode>(1)
  const mediaStreamRef = useRef<MediaStream | null>(null)
  const audioContextRef = useRef<AudioContext | null>(null)
  const processorRef = useRef<ScriptProcessorNode | null>(null)
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null)
  const gainRef = useRef<GainNode | null>(null)
  const micInputGainRef = useRef<GainNode | null>(null)
  const speakerCaptureActiveRef = useRef(false)
  const getUserMediaAbortRef = useRef<AbortController | null>(null)
  const streamingRef = useRef(false)
  const cancelHoldRef = useRef(false)
  const sessionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const sessionCompleteRef = useRef(false)
  /** Last partial from Transcribe; used if session_stopped has empty finals (short utterances). */
  const lastPartialTranscriptRef = useRef('')
  const speechDetectedRef = useRef(false)
  const lastLoudAtRef = useRef(0)
  const recordingStartedAtRef = useRef(0)
  const silenceStopTriggeredRef = useRef(false)
  const pcmSendBufferRef = useRef<Uint8Array>(new Uint8Array(0))
  const stopRecordingRef = useRef<() => void>(() => {})
  /** When the mic stops until Nova Lite / rule-normalized command is shown in chat. */
  const speechStoppedAtRef = useRef<number | null>(null)
  /** When transcript reached chat until assistant rows (Nova intent + entities). */
  const intentStartedAtRef = useRef<number | null>(null)
  const transcriptReadyReceivedRef = useRef(false)
  const displayTranscriptRef = useRef('')

  const consumeTranscribeProcessingMs = useCallback((): number | undefined => {
    const started = speechStoppedAtRef.current
    speechStoppedAtRef.current = null
    if (started == null) return undefined
    return Math.max(0, Date.now() - started)
  }, [])

  const cbRef = useRef({ onPartial, onTranscriptReady, onFinal, onError, getActiveDocumentContext })
  cbRef.current = { onPartial, onTranscriptReady, onFinal, onError, getActiveDocumentContext }

  const runtimeRef = useRef({
    apiBaseUrl,
    clientId,
    tenantId,
    sessionId,
    getMedicalLinkCredentials,
    getActiveDocumentContext,
    languageOptions,
    preferredLanguage,
    sampleRate,
    transcriptionOnly,
    finishingTranscriptionHint,
    intentProcessingHint,
  })
  runtimeRef.current = {
    apiBaseUrl,
    clientId,
    tenantId,
    sessionId,
    getMedicalLinkCredentials,
    getActiveDocumentContext,
    languageOptions,
    preferredLanguage,
    sampleRate,
    transcriptionOnly,
    finishingTranscriptionHint,
    intentProcessingHint,
  }

  const setVoice = useCallback((s: VoiceStreamStatus, hint = '') => {
    if (s === 'transcribing') {
      if (speechStoppedAtRef.current == null) {
        speechStoppedAtRef.current = Date.now()
      }
    } else if (s === 'recording' || s === 'connecting' || s === 'requesting_permission') {
      speechStoppedAtRef.current = null
      intentStartedAtRef.current = null
      transcriptReadyReceivedRef.current = false
      displayTranscriptRef.current = ''
    }
    statusRef.current = s
    setStatus(s)
    setStatusHint(hint)
  }, [])

  const cleanupAudio = useCallback(() => {
    try {
      processorRef.current?.disconnect()
    } catch {
      // ignore
    }
    try {
      sourceRef.current?.disconnect()
    } catch {
      // ignore
    }
    try {
      micInputGainRef.current?.disconnect()
    } catch {
      // ignore
    }
    try {
      gainRef.current?.disconnect()
    } catch {
      // ignore
    }
    processorRef.current = null
    sourceRef.current = null
    micInputGainRef.current = null
    gainRef.current = null

    const rec = mediaRecorderRef.current
    mediaRecorderRef.current = null
    if (rec && rec.state !== 'inactive') {
      try {
        rec.onstop = null
        rec.stop()
      } catch {
        // ignore
      }
    }

    const ctx = audioContextRef.current
    audioContextRef.current = null
    if (ctx && ctx.state !== 'closed') {
      void ctx.close()
    }

    try {
      mediaStreamRef.current?.getTracks().forEach((t) => t.stop())
    } catch {
      // ignore
    }
    mediaStreamRef.current = null
    recorderChunksRef.current = []
    streamingRef.current = false
    speechDetectedRef.current = false
    lastLoudAtRef.current = 0
    recordingStartedAtRef.current = 0
    silenceStopTriggeredRef.current = false
    speakerCaptureActiveRef.current = false
    resetPcmSendBuffer(pcmSendBufferRef)
  }, [])

  const cleanupWs = useCallback(() => {
    const ws = wsRef.current
    wsRef.current = null
    if (!ws) return
    ws.onopen = null
    ws.onerror = null
    ws.onmessage = null
    ws.onclose = null
    try {
      if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
        ws.close()
      }
    } catch {
      // ignore
    }
  }, [])

  const clearSessionTimer = useCallback(() => {
    if (sessionTimerRef.current) {
      clearTimeout(sessionTimerRef.current)
      sessionTimerRef.current = null
    }
  }, [])

  const uploadBatchRecording = useCallback(async (blob: Blob, mime: string) => {
    const rt = runtimeRef.current
    const base = (rt.apiBaseUrl ?? '').trim()
    if (!base) {
      cbRef.current.onError('API base URL is not configured for batch transcription.')
      setVoice('error', '')
      return
    }

    const meta = buildVoiceMetadataPayload({
      sampleRate: rt.sampleRate,
      languageOptions: rt.languageOptions,
      preferredLanguage: rt.preferredLanguage,
      clientId: rt.clientId,
      tenantId: rt.tenantId,
      sessionId: rt.sessionId,
      getMedicalLinkCredentials: rt.getMedicalLinkCredentials,
      getActiveDocumentContext: rt.getActiveDocumentContext,
      transcriptionOnly: rt.transcriptionOnly,
    })

    const form = new FormData()
    form.append('audio', blob, `utterance.${extensionForMime(mime)}`)
    form.append('metadata_json', JSON.stringify(meta))
    if (rt.transcriptionOnly) form.append('transcription_only', '1')

    const bearer = rt.getMedicalLinkCredentials?.().piBearerToken
    sessionCompleteRef.current = true
    clearSessionTimer()

    await logBatchUploadForAws(blob, mime)

    try {
      const result = await postTranscribeVoice(base, form, bearer)
      const full = (result.full_transcript ?? '').trim()
      const display = (
        result.llm_normalized_transcript ??
        result.display_transcript ??
        result.rule_normalized_transcript ??
        full
      ).trim()
      const assistantMessages = result.assistant_messages ?? []
      const llmTokenUsage = result.llm_token_usage ?? null
      const hasAssistant = Array.isArray(assistantMessages) && assistantMessages.length > 0
      const transcribeProcessingMs = consumeTranscribeProcessingMs()
      const normShownEarly = Boolean(display && cbRef.current.onTranscriptReady)
      if (normShownEarly) {
        transcriptReadyReceivedRef.current = true
        intentStartedAtRef.current = Date.now()
        cbRef.current.onTranscriptReady?.(display, full, transcribeProcessingMs)
      }
      if (display || full || hasAssistant) {
        const intentProcessingMs =
          intentStartedAtRef.current != null
            ? Math.max(0, Date.now() - intentStartedAtRef.current)
            : undefined
        intentStartedAtRef.current = null
        transcriptReadyReceivedRef.current = false
        const maybe = cbRef.current.onFinal(
          display || full,
          assistantMessages,
          llmTokenUsage,
          normShownEarly ? undefined : transcribeProcessingMs,
          intentProcessingMs,
        ) as void | Promise<void>
        void Promise.resolve(maybe).finally(() => setVoice('idle', ''))
      } else {
        cbRef.current.onError('No speech detected.')
        setVoice('idle', '')
      }
    } catch (e) {
      cbRef.current.onError(e instanceof Error ? e.message : String(e))
      setVoice('error', '')
    }
  }, [clearSessionTimer, consumeTranscribeProcessingMs, setVoice])

  const stopBatchRecording = useCallback(() => {
    streamingRef.current = false
    const rec = mediaRecorderRef.current
    const mime = recorderMimeRef.current || 'audio/webm'
    if (!rec || rec.state === 'inactive') {
      cleanupAudio()
      clearSessionTimer()
      setVoice('idle', '')
      return
    }

    setVoice('transcribing', 'Transcribing…')
    sessionTimerRef.current = setTimeout(() => {
      sessionTimerRef.current = null
      if (statusRef.current === 'transcribing' && !sessionCompleteRef.current) {
        cbRef.current.onError('Timed out waiting for batch transcription.')
        setVoice('error', '')
        cleanupAudio()
      }
    }, BATCH_SESSION_WAIT_MS)

    rec.onstop = () => {
      const chunks = recorderChunksRef.current
      recorderChunksRef.current = []
      mediaRecorderRef.current = null
      cleanupAudio()
      const blob = new Blob(chunks, { type: mime })
      if (!blob.size) {
        clearSessionTimer()
        cbRef.current.onError('No speech detected.')
        setVoice('idle', '')
        return
      }
      void uploadBatchRecording(blob, mime)
    }

    try {
      rec.stop()
    } catch {
      clearSessionTimer()
      cbRef.current.onError('Could not finish batch recording.')
      setVoice('error', '')
      cleanupAudio()
    }
  }, [cleanupAudio, clearSessionTimer, setVoice, uploadBatchRecording])

  const stopRecording = useCallback(() => {
    cancelHoldRef.current = true
    getUserMediaAbortRef.current?.abort()
    getUserMediaAbortRef.current = null

    if (activeTranscribeModeRef.current === 2) {
      stopBatchRecording()
      return
    }

    cleanupAudio()

    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      cleanupWs()
      clearSessionTimer()
      return
    }

    setVoice('transcribing', runtimeRef.current.finishingTranscriptionHint || 'Finishing…')
    try {
      flushStreamingPcm(ws, pcmSendBufferRef)
      ws.send(JSON.stringify({ type: 'stop' }))
    } catch {
      cbRef.current.onError('Could not send stop to voice service.')
      setVoice('error', '')
      cleanupWs()
      clearSessionTimer()
      return
    }

    sessionTimerRef.current = setTimeout(() => {
      sessionTimerRef.current = null
      if (statusRef.current === 'transcribing' && !sessionCompleteRef.current) {
        cbRef.current.onError('Timed out waiting for transcript.')
        setVoice('error', '')
      }
      cleanupWs()
    }, SESSION_WAIT_MS)
  }, [cleanupAudio, cleanupWs, clearSessionTimer, setVoice, stopBatchRecording])

  useEffect(() => {
    stopRecordingRef.current = stopRecording
  }, [stopRecording])

  const cancelInFlightStart = useCallback(() => {
    cancelHoldRef.current = true
    getUserMediaAbortRef.current?.abort()
    getUserMediaAbortRef.current = null
    cleanupAudio()
    cleanupWs()
    clearSessionTimer()
    silenceStopTriggeredRef.current = false
    setVoice('idle', '')
  }, [cleanupAudio, cleanupWs, clearSessionTimer, setVoice])

  const startRecording = useCallback(async (options?: StartRecordingOptions) => {
    const s = statusRef.current
    if (s === 'connecting' || s === 'recording' || s === 'transcribing') return

    const speakerCapture = Boolean(options?.speakerPlaybackCapture)
    speakerCaptureActiveRef.current = speakerCapture

    const mode: TranscribeMode = transcribeMode === 2 ? 2 : 1
    activeTranscribeModeRef.current = mode
    logAwsTranscribeMode(mode)

    if (mode === 1) {
      const url = websocketUrl.trim()
      if (!url) {
        setVoice('error', '')
        cbRef.current.onError('WebSocket URL is not configured (websocketUrl or VITE_WS_BASE_URL).')
        return
      }
    } else {
      const base = (apiBaseUrl ?? '').trim()
      if (!base) {
        setVoice('error', '')
        cbRef.current.onError('API base URL is not configured for batch transcription.')
        return
      }
      if (!pickRecorderMimeType()) {
        setVoice('error', '')
        cbRef.current.onError('This browser does not support audio recording for batch transcription.')
        return
      }
    }

    if (s === 'error') {
      setVoice('idle', '')
    }

    cancelHoldRef.current = false
    sessionCompleteRef.current = false
    lastPartialTranscriptRef.current = ''
    setVoice('requesting_permission', '')

    const requestMicStream = async (
      allowNotReadableRetry: boolean,
      speakerPlaybackCapture: boolean,
    ): Promise<MediaStream | null> => {
      const ac = new AbortController()
      getUserMediaAbortRef.current = ac

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: micAudioConstraints(speakerPlaybackCapture),
          signal: ac.signal,
        })
        getUserMediaAbortRef.current = null
        return stream
      } catch (e) {
        getUserMediaAbortRef.current = null
        if ((e as Error).name === 'AbortError') {
          setVoice('idle', '')
          return null
        }
        const name = (e as DOMException).name || (e as Error).name || ''
        if (name === 'NotReadableError' && allowNotReadableRetry && !cancelHoldRef.current) {
          cleanupAudio()
          cleanupWs()
          await wait(RECOVERABLE_MIC_RETRY_MS)
          if (cancelHoldRef.current) {
            setVoice('idle', '')
            return null
          }
          setVoice('requesting_permission', '')
          return requestMicStream(false, speakerPlaybackCapture)
        }
        setVoice('error', '')
        cbRef.current.onError(describeMicrophoneAccessError(name))
        return null
      }
    }

    if (typeof window !== 'undefined' && !window.isSecureContext) {
      setVoice('error', '')
      cbRef.current.onError(
        'Microphone requires a secure context: use HTTPS on your site (not plain HTTP on an IP or hostname).',
      )
      return
    }

    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setVoice('error', '')
      cbRef.current.onError('Microphone API is not available in this browser or context.')
      return
    }

    const stream = await requestMicStream(true, speakerCapture)
    if (!stream) {
      return
    }

    if (cancelHoldRef.current) {
      stream.getTracks().forEach((t) => t.stop())
      setVoice('idle', '')
      return
    }

    mediaStreamRef.current = stream

    if (mode === 2) {
      const mime = pickRecorderMimeType()
      recorderMimeRef.current = mime
      recorderChunksRef.current = []

      const recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined)
      mediaRecorderRef.current = recorder
      recorder.ondataavailable = (ev) => {
        if (ev.data.size > 0) recorderChunksRef.current.push(ev.data)
      }
      recorder.start(250)

      let audioContext: AudioContext
      try {
        audioContext = new AudioContext()
      } catch {
        cleanupAudio()
        setVoice('error', '')
        cbRef.current.onError('Could not start audio processing for batch recording.')
        return
      }
      audioContextRef.current = audioContext
      logBatchRecordingStarted({
        micSampleRateHz: micTrackSampleRateHz(stream),
        audioContextSampleRateHz: audioContext.sampleRate,
        mime,
        widgetSampleRate: sampleRate,
      })

      const source = audioContext.createMediaStreamSource(stream)
      const processor = audioContext.createScriptProcessor(4096, 1, 1)
      const gain = audioContext.createGain()
      gain.gain.value = 0
      if (speakerCapture) {
        const micGain = audioContext.createGain()
        micGain.gain.value = SPEAKER_CAPTURE_MIC_GAIN
        source.connect(micGain)
        micGain.connect(processor)
        micInputGainRef.current = micGain
      } else {
        source.connect(processor)
      }
      processor.connect(gain)
      gain.connect(audioContext.destination)
      sourceRef.current = source
      processorRef.current = processor
      gainRef.current = gain

      recordingStartedAtRef.current = performance.now()
      speechDetectedRef.current = false
      silenceStopTriggeredRef.current = false
      lastLoudAtRef.current = recordingStartedAtRef.current

      const batchSilence =
        silenceAutoStopMs === false
          ? false
          : Math.min(Number(silenceAutoStopMs), BATCH_SILENCE_AUTO_STOP_MS)
      const silenceEnabled = batchSilence !== false && !speakerCapture
      const silenceMs = silenceEnabled ? Number(batchSilence) : 0
      const rmsTh = silenceRmsThreshold
      const grace = silenceGraceMs

      processor.onaudioprocess = (e) => {
        if (!streamingRef.current) return
        const input = e.inputBuffer.getChannelData(0)
        if (silenceEnabled && !silenceStopTriggeredRef.current && !speakerCaptureActiveRef.current) {
          const now = performance.now()
          const rms = bufferRms(input)
          if (rms > rmsTh) {
            speechDetectedRef.current = true
            lastLoudAtRef.current = now
          }
          if (
            speechDetectedRef.current &&
            now - recordingStartedAtRef.current > grace &&
            now - lastLoudAtRef.current >= silenceMs
          ) {
            silenceStopTriggeredRef.current = true
            stopRecordingRef.current()
          }
        }
      }

      streamingRef.current = true
      setVoice('recording', '')
      void audioContext.resume()
      return
    }

    const url = websocketUrl.trim()
    setVoice('connecting', '')

    const ws = new WebSocket(url)
    ws.binaryType = 'arraybuffer'
    wsRef.current = ws

    try {
      await new Promise<void>((resolve, reject) => {
        const t = setTimeout(
          () =>
            reject(
              new Error(
                `WebSocket connect timeout (12s) — ${url}. If the API works but voice does not, the reverse proxy must forward WebSocket upgrades to uvicorn (see backend README).`,
              ),
            ),
          12000,
        )
        let settled = false
        const fail = (msg: string) => {
          if (settled) return
          settled = true
          clearTimeout(t)
          reject(new Error(msg))
        }
        ws.onopen = () => {
          if (settled) return
          settled = true
          clearTimeout(t)
          resolve()
        }
        ws.onerror = () => {
          fail(`WebSocket connection failed (${url})`)
        }
        ws.onclose = (ev) => {
          if (settled) return
          const hint =
            ev.code === 1008
              ? ' (origin not allowed on server — check WEBSOCKET_ALLOWED_ORIGINS)'
              : ev.code === 1006
                ? ' (abnormal closure — often proxy/firewall or TLS)'
                : ''
          fail(`WebSocket closed before open: code ${ev.code}${ev.reason ? ` ${ev.reason}` : ''}${hint}`)
        }
      })
    } catch (e) {
      cleanupWs()
      stream.getTracks().forEach((t) => t.stop())
      mediaStreamRef.current = null
      setVoice('error', '')
      cbRef.current.onError(e instanceof Error ? e.message : String(e))
      return
    }

    if (cancelHoldRef.current || wsRef.current !== ws) {
      cleanupWs()
      stream.getTracks().forEach((t) => t.stop())
      mediaStreamRef.current = null
      setVoice('idle', '')
      return
    }

    ws.onmessage = (ev) => {
      if (typeof ev.data !== 'string') return
      try {
        const msg = JSON.parse(ev.data) as {
          type?: string
          text?: string
          full_transcript?: string
          message?: string
        }
        if (msg.type === 'partial_transcript' && msg.text != null) {
          lastPartialTranscriptRef.current = msg.text
          setStatusHint(msg.text)
          cbRef.current.onPartial?.(msg.text)
        } else if (msg.type === 'final_transcript' && msg.text != null) {
          lastPartialTranscriptRef.current = msg.text
          setStatusHint(msg.text)
        } else if (msg.type === 'transcript_ready') {
          const ready = msg as {
            display_transcript?: string
            full_transcript?: string
            llm_normalized_transcript?: string
            rule_normalized_transcript?: string
          }
          const display = (
            ready.display_transcript ??
            ready.llm_normalized_transcript ??
            ready.rule_normalized_transcript ??
            ''
          ).trim()
          if (!display) return
          displayTranscriptRef.current = display
          transcriptReadyReceivedRef.current = true
          const transcribeProcessingMs = consumeTranscribeProcessingMs()
          intentStartedAtRef.current = Date.now()
          lastPartialTranscriptRef.current = ''
          setStatusHint(runtimeRef.current.intentProcessingHint || '')
          statusRef.current = 'transcribing'
          setStatus('transcribing')
          cbRef.current.onTranscriptReady?.(
            display,
            (ready.full_transcript ?? '').trim(),
            transcribeProcessingMs,
          )
        } else if (msg.type === 'session_stopped') {
          sessionCompleteRef.current = true
          clearSessionTimer()
          let full = (msg.full_transcript ?? '').trim()
          if (!full && lastPartialTranscriptRef.current.trim()) {
            full = lastPartialTranscriptRef.current.trim()
          }
          lastPartialTranscriptRef.current = ''
          const stopped = msg as {
            assistant_messages?: AssistantChatMessage[]
            llm_token_usage?: Record<string, unknown> | null
            display_transcript?: string
          }
          const assistantMessages = stopped.assistant_messages
          const llmTokenUsage = stopped.llm_token_usage ?? null
          const hasAssistant =
            Array.isArray(assistantMessages) && assistantMessages.length > 0
          const intentProcessingMs =
            intentStartedAtRef.current != null
              ? Math.max(0, Date.now() - intentStartedAtRef.current)
              : undefined
          intentStartedAtRef.current = null
          const transcribeProcessingMs = transcriptReadyReceivedRef.current
            ? undefined
            : consumeTranscribeProcessingMs()
          const displayText = (
            displayTranscriptRef.current ||
            stopped.display_transcript ||
            full
          ).trim()
          transcriptReadyReceivedRef.current = false
          displayTranscriptRef.current = ''
          cleanupWs()
          // Run onFinal/onError before returning to idle so intent navigation runs while the session
          // is still logically "finishing" (matches typed /chat flow) and no effect sees idle first.
          if (displayText || full || hasAssistant) {
            const maybe = cbRef.current.onFinal(
              displayText || full,
              assistantMessages ?? [],
              llmTokenUsage,
              transcribeProcessingMs,
              intentProcessingMs,
            ) as
              | void
              | Promise<void>
            void Promise.resolve(maybe).finally(() => {
              setVoice('idle', '')
            })
          } else {
            cbRef.current.onError('No speech detected.')
            setVoice('idle', '')
          }
        } else if (msg.type === 'error') {
          sessionCompleteRef.current = true
          clearSessionTimer()
          cleanupWs()
          setVoice('error', '')
          cbRef.current.onError(msg.message || 'Voice service error')
        }
      } catch {
        // ignore parse errors
      }
    }

    ws.onclose = () => {
      if (sessionCompleteRef.current) return
      if (statusRef.current === 'transcribing') {
        clearSessionTimer()
        setVoice('error', '')
        cbRef.current.onError('Connection closed before transcript was ready.')
      }
    }

    const startPayload: Record<string, unknown> = {
      type: 'start',
      sample_rate: sampleRate,
      language_options: languageOptions,
      preferred_language: preferredLanguage,
      client_id: clientId,
      tenant_id: tenantId,
      session_id: sessionId,
      ...clientReminderRuntimePayload(),
    }
    const ml = getMedicalLinkCredentials?.()
    if (ml) {
      startPayload.pi_bearer_token = ml.piBearerToken
      startPayload.logged_user_id = ml.loggedUserId
      startPayload.med_center_id = ml.medCenterId
      const fb = (ml.senderFbUid ?? '').trim()
      if (fb) {
        startPayload.sender_fb_uid = fb
      }
    }
    const docCtx = getActiveDocumentContext?.()
    if (docCtx) {
      const did = (docCtx.id ?? '').trim()
      const dname = (docCtx.name ?? '').trim()
      const durl = (docCtx.url ?? '').trim()
      const dmime = (docCtx.mime ?? '').trim()
      const dex = (docCtx.examDate ?? '').trim()
      const dfolder = (docCtx.folderName ?? '').trim()
      if (did) startPayload.active_document_id = did
      if (dname) startPayload.active_document_name = dname
      if (durl) startPayload.active_document_url = durl
      if (dmime) startPayload.active_document_mime_type = dmime
      if (dex) startPayload.active_document_exam_date = dex
      if (dfolder) startPayload.active_document_folder = dfolder
    }
    if (transcriptionOnly) {
      startPayload.transcription_only = true
    }
    ws.send(JSON.stringify(startPayload))

    let audioContext: AudioContext
    try {
      audioContext = new AudioContext({ sampleRate })
    } catch {
      audioContext = new AudioContext()
    }
    audioContextRef.current = audioContext
    resetPcmSendBuffer(pcmSendBufferRef)
    const pcmChunkBytes = streamingPcmChunkBytes(sampleRate)
    logStreamingRecordingStarted({
      micSampleRateHz: micTrackSampleRateHz(stream),
      audioContextSampleRateHz: audioContext.sampleRate,
      targetSampleRateHz: sampleRate,
      pcmChunkBytes,
      preferredLanguage,
    })

    const source = audioContext.createMediaStreamSource(stream)
    const processor = audioContext.createScriptProcessor(4096, 1, 1)
    const gain = audioContext.createGain()
    gain.gain.value = 0

    if (speakerCapture) {
      const micGain = audioContext.createGain()
      micGain.gain.value = SPEAKER_CAPTURE_MIC_GAIN
      source.connect(micGain)
      micGain.connect(processor)
      micInputGainRef.current = micGain
    } else {
      source.connect(processor)
    }
    processor.connect(gain)
    gain.connect(audioContext.destination)

    sourceRef.current = source
    processorRef.current = processor
    gainRef.current = gain

    recordingStartedAtRef.current = performance.now()
    speechDetectedRef.current = speakerCapture
    silenceStopTriggeredRef.current = false
    lastLoudAtRef.current = recordingStartedAtRef.current

    const inRate = audioContext.sampleRate
    const silenceEnabled = silenceAutoStopMs !== false && !speakerCapture
    const silenceMs = silenceEnabled ? Number(silenceAutoStopMs) : 0
    const rmsTh = silenceRmsThreshold
    const grace = silenceGraceMs

    processor.onaudioprocess = (e) => {
      if (!streamingRef.current || ws.readyState !== WebSocket.OPEN) return
      const input = e.inputBuffer.getChannelData(0)

      if (silenceEnabled && !silenceStopTriggeredRef.current && !speakerCaptureActiveRef.current) {
        const now = performance.now()
        const rms = bufferRms(input)
        if (rms > rmsTh) {
          speechDetectedRef.current = true
          lastLoudAtRef.current = now
        }
        if (
          speechDetectedRef.current &&
          now - recordingStartedAtRef.current > grace &&
          now - lastLoudAtRef.current >= silenceMs
        ) {
          silenceStopTriggeredRef.current = true
          stopRecordingRef.current()
          return
        }
      }

      const resampled = linearResample(input, inRate, sampleRate)
      const pcm = floatTo16BitPCM(resampled)
      try {
        enqueueStreamingPcm(ws, pcmSendBufferRef, pcm, pcmChunkBytes)
      } catch {
        // ignore
      }
    }

    streamingRef.current = true
    setVoice('recording', '')

    void audioContext.resume()
  }, [
    websocketUrl,
    apiBaseUrl,
    transcribeMode,
    clientId,
    tenantId,
    sessionId,
    getMedicalLinkCredentials,
    getActiveDocumentContext,
    transcriptionOnly,
    languageOptions,
    preferredLanguage,
    sampleRate,
    silenceAutoStopMs,
    silenceRmsThreshold,
    silenceGraceMs,
    cleanupAudio,
    cleanupWs,
    clearSessionTimer,
    setVoice,
  ])

  const toggleMic = useCallback(() => {
    const s = statusRef.current
    if (s === 'recording') {
      stopRecording()
      return
    }
    if (s === 'transcribing') return
    if (s === 'connecting' || s === 'requesting_permission') {
      cancelInFlightStart()
      return
    }
    if (s === 'idle' || s === 'error') {
      void startRecording()
    }
  }, [stopRecording, cancelInFlightStart, startRecording])

  useEffect(() => {
    return () => {
      clearSessionTimer()
      cleanupAudio()
      cleanupWs()
    }
  }, [cleanupAudio, cleanupWs, clearSessionTimer])

  return {
    status,
    statusHint,
    toggleMic,
    startRecording,
    stopRecording,
  }
}
